/*
 * Overview KPI tiles: pure model, no React and nothing that touches `window`. From the last 7 days of builds it derives
 * today's count, the success rate, and the copy of the two tiles. Median build time and queue wait come back when the
 * API reports them (they were placeholders, never numbers).
 */
export interface KpiBuild {
  status: string;
  createdAt: string;
}

export interface KpiStats {
  today: number;
  todayLive: number;
  /** Percent of finished builds that succeeded; `null` when none finished. */
  rate: number | null;
  finished: number;
  /** More pages exist than were loaded, so the rate covers only the first rows. */
  partial: boolean;
}

export interface KpiTile {
  label: string;
  short: string;
  icon: string;
  testId: string;
  value: string | null;
  bar: number;
  reason: string;
}

/** What the tiles know when there are no stats: the builds query failed, there is no project, or it is still loading. */
export interface KpiContext {
  isError: boolean;
  hasProject: boolean;
}

const FINISHED = ['succeeded', 'failed', 'errored', 'timed_out'];
const LIVE = ['running', 'verifying', 'dispatching', 'provider_queued'];

/** `rows` undefined (not loaded yet) gives `null`. "Today" starts at local midnight of `now`. */
export function kpiStats(rows: KpiBuild[] | undefined, hasNextPage: boolean, now: Date): KpiStats | null {
  if (!rows) return null;
  const dayStart = new Date(now);
  dayStart.setHours(0, 0, 0, 0);
  const today = rows.filter((b) => new Date(b.createdAt).getTime() >= dayStart.getTime());
  const finished = rows.filter((b) => FINISHED.includes(b.status));
  const ok = finished.filter((b) => b.status === 'succeeded').length;
  return {
    today: today.length,
    todayLive: today.filter((b) => LIVE.includes(b.status)).length,
    rate: finished.length > 0 ? Math.round((ok / finished.length) * 100) : null,
    finished: finished.length,
    partial: hasNextPage,
  };
}

function unknownReason(ctx: KpiContext): string {
  if (ctx.isError) return 'could not load builds';
  return ctx.hasProject ? 'loading' : 'no project yet';
}

function todayReason(stats: KpiStats): string {
  if (stats.todayLive > 0) return `${stats.todayLive} in progress`;
  return stats.today === 0 ? 'nothing requested yet today' : 'all settled';
}

function rateReason(stats: KpiStats): string {
  if (stats.rate === null) return 'no finished builds in 7 days';
  return `${stats.finished} finished${stats.partial ? ' (first 200)' : ''}`;
}

function buildsTodayTile(stats: KpiStats | null, ctx: KpiContext): KpiTile {
  return {
    label: 'Builds today',
    short: 'Builds today',
    icon: 'construction',
    testId: 'kpi-builds-today',
    value: stats ? String(stats.today) : null,
    bar: stats ? Math.min(100, stats.today * 10) : 0,
    reason: stats ? todayReason(stats) : unknownReason(ctx),
  };
}

function successRateTile(stats: KpiStats | null, ctx: KpiContext): KpiTile {
  return {
    label: 'Success rate (7d)',
    short: 'Success rate',
    icon: 'check_circle',
    testId: 'kpi-success-rate',
    value: stats && stats.rate !== null ? `${stats.rate}%` : null,
    bar: stats?.rate ?? 0,
    reason: stats ? rateReason(stats) : unknownReason(ctx),
  };
}

/** The tiles of screen 01 that have real numbers: builds today and the 7-day success rate. */
export function kpiTiles(stats: KpiStats | null, ctx: KpiContext): KpiTile[] {
  return [buildsTodayTile(stats, ctx), successRateTile(stats, ctx)];
}
